// =============================================================================
// Presence shadow-write — record that somebody was actually in the shop.
//
// WHY THIS EXISTS. When a Google review lands we want to credit whoever was
// working. Matching the customer's words to a name is already done. The missing
// half is knowing who was in the building, and the only evidence we have is that
// staff tap their PIN into the tablet all day. Those taps currently land in a
// Google Sheet and nowhere else. This writes a copy into Supabase so that in a
// few weeks there's enough history to credit reviews properly.
//
// WHAT IT IS NOT. It changes nothing a staff member sees. Nothing reads these
// rows. It's write-only, it soaks, and the leaderboard that consumes it is a
// later job.
//
// THE THREE RULES IT OBEYS
//
//  1. IT CAN NEVER BREAK A TAP. Every call is fire-and-forget: the till has
//     already closed the modal and run the action before this does anything.
//     Every error is swallowed. If Supabase is down, staff never know.
//
//  2. IT NEVER SENDS A PIN TO THE DATABASE. The PIN goes to the same login
//     function the till already uses, which checks it server-side and hands back
//     a short-lived pass carrying that person's id. The database copies the id
//     off that pass. A row can therefore never carry a PIN, and a tablet can
//     never claim a tap was somebody else.
//
//  3. IT'S INERT UNLESS IT'S A REAL KIOSK. Local dev, previews and the staging
//     worker do nothing at all — same host gate the stock shadow-write uses.
//
// WHY IT HAS ITS OWN CONNECTION (this one matters — it's a bug I caught in
// review rather than a preference). Tapping a PIN mints a short-lived pass, and
// that pass is what the database uses to decide whose name goes on a row. The
// till shares ONE connection for everything, so if presence minted a pass on
// that shared connection it could land in the middle of somebody else's
// half-finished stock or sell-out write and quietly put the wrong person's name
// on it. Two staff tapping within half a second of each other is all it would
// take. So presence gets its own separate connection: it can mint whatever pass
// it likes and the till's connection never notices. The session is remembered
// between page loads so this doesn't churn through anonymous accounts.
//
// KILL SWITCH: set PRESENCE_ENABLED to false below. That is the whole revert.
// =============================================================================
import { createClient } from "@supabase/supabase-js";
import { getSupabaseConfig } from "../config/supabase.js";
import { record as recordActivity } from "./activity.js";
import { dualRunEnabled } from "./stockSync.js";

// ---- the one-line kill switch ----------------------------------------------
// Flip to false and redeploy: every call below returns immediately and not a
// single network request is made. Nothing else has to change.
export const PRESENCE_ENABLED = true;

// A tap is worth recording once. A checklist run fires a burst of them from the
// same person within seconds, and each one would otherwise mean a fresh login
// round-trip. If the same person taps again inside this window we reuse the pass
// they already have. Purely an efficiency measure — every tap still gets a row.
const PASS_REUSE_MS = 60_000;

let lastPass = null;   // { pin, staffId, at }

// Taps are serialised through this chain. Minting a pass changes who this
// connection is, so two taps landing together must not interleave — otherwise a
// row could be stamped with the wrong person. One at a time, in order.
let queue = Promise.resolve();

// ---- our own connection, deliberately separate from the till's --------------
const cfg = getSupabaseConfig();
const AUTH_PIN_URL = `${cfg.url}/functions/v1/auth-pin`;

// persistSession is ON here (the till's shared client has it off). Reusing the
// same anonymous account across page loads means a tablet doesn't mint a fresh
// one every time it reloads. The PIN pass itself still expires server-side after
// ~15 minutes regardless, so nothing is left unlocked.
const psb = createClient(cfg.url, cfg.anonKey, {
  auth: { persistSession: true, autoRefreshToken: true, storageKey: "rolled.presence.auth" },
});

async function ownSession() {
  const { data } = await psb.auth.getSession();
  if (data?.session) return data.session;
  const { data: signed, error } = await psb.auth.signInAnonymously();
  if (error) throw error;
  return signed.session;
}

// Who does our connection currently count as? Read straight off the token.
async function ownPass() {
  const { data } = await psb.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) return null;
  try {
    const claims = JSON.parse(atob(token.split(".")[1]));
    if (claims.app_session === "true") return { staffId: claims.staff_id };
  } catch (e) { /* not a readable token */ }
  return null;
}

// Same handshake the till's login uses, but on OUR connection — the PIN is
// checked server-side and only a pass comes back. Nothing is stored here.
async function ownLogin(pin, siteKey) {
  const session = await ownSession();
  const res = await fetch(AUTH_PIN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${session.access_token}`,
      "apikey": cfg.anonKey,
    },
    body: JSON.stringify({ pin, functionKey: null, siteId: null, siteKey }),
  });
  const out = await res.json().catch(() => ({ ok: false }));
  if (!out.ok) return out;
  await psb.auth.refreshSession();   // pull the token the access-token hook stamped
  return out;
}

// ---- is this thing on? ------------------------------------------------------
function active() {
  if (!PRESENCE_ENABLED) return false;
  try {
    // ?presence=off kills it for one tablet without a deploy; ?presence=on forces
    // it on for a pre-shift test on a non-kiosk host.
    if (typeof window !== "undefined") {
      const q = new URLSearchParams(window.location.search).get("presence");
      if (q === "off") return false;
      if (q === "on") return true;
    }
  } catch (e) { /* no window, no query string — fall through */ }
  return dualRunEnabled();
}

// ---- get a pass carrying THIS person's id -----------------------------------
// Returns the staff id we're about to be stamped as, or null if we couldn't get
// one. Reuses a recent pass for the same PIN, but only after confirming the
// tablet's token still belongs to that same person — if anyone else has tapped
// in between, we mint a fresh one rather than mis-credit the tap.
async function passFor(pin, siteKey) {
  if (lastPass && lastPass.pin === pin && Date.now() - lastPass.at < PASS_REUSE_MS) {
    const live = await ownPass();
    if (live && live.staffId === lastPass.staffId) return lastPass.staffId;
  }
  const res = await ownLogin(pin, siteKey);
  if (!res || !res.ok) return null;
  const live = await ownPass();
  if (!live || !live.staffId) return null;
  lastPass = { pin, staffId: live.staffId, at: Date.now() };
  return live.staffId;
}

// ---- the one function the screens call --------------------------------------
// recordTap({ siteKey, pin, kind, source, functionKey })
//
//   siteKey     'tc' | 'arndale' | 'meadowhall' | 'mcr' (or the legacy 'kitchen'
//               / 'trafford' spellings — the database folds them)
//   pin         the PIN that was just tapped. Used to obtain a pass and then
//               dropped. It is never sent to the database and never stored here.
//   kind        'action' for a per-action gate (what attribution counts),
//               'login' for signing in, 'clock_in' / 'clock_out' for the clock.
//   source      free text for forensics — 'foh-action', 'kitchen-action', 'login'
//   functionKey which screen or gate, when the caller knows it
//
// Call it WITHOUT awaiting. It returns a promise so tests can wait, but the till
// should fire and forget.
export function recordTap({ siteKey, pin, kind = "action", source = null, functionKey = null }) {
  if (!active() || !siteKey || !pin) return Promise.resolve({ ok: false, skipped: true });

  queue = queue.then(async () => {
    try {
      const staffId = await passFor(pin, siteKey);
      if (!staffId) return { ok: false, error: "no pass" };
      // staff_id and site_id are filled in by the database from the pass and the
      // shop key — deliberately not sent from here.
      const { error } = await psb.from("staff_presence").insert({
        site: siteKey,
        kind,
        source,
        function_key: functionKey,
        occurred_at: new Date().toISOString(),
        origin: "kiosk",
      });
      if (error) {
        console.warn("[presence] write failed:", error.message);
        return { ok: false, error };
      }
      await alsoLog({ siteKey, action: source || kind, kind: kind === "login" ? "login" : "action", detail: functionKey });
      return { ok: true };
    } catch (e) {
      console.warn("[presence] threw:", e?.message || e);
      return { ok: false, error: e };
    }
  }).catch(() => ({ ok: false }));   // the chain must never end up rejected

  return queue;
}

// ---- the audit line that goes with a tap ------------------------------------
// Presence answers "who was in the building"; the activity log answers "who did
// what". They're different questions and the Command Centre asks them on
// different screens, so a tap writes both.
//
// ⚠️ IT WRITES ON PRESENCE'S OWN CONNECTION (`psb`), NOT THE TILL'S SHARED ONE.
// That's the whole reason presence has its own connection: who a row gets signed
// by is decided server-side from the login pass on the connection that sent it,
// and the shared one is being used by stock and sell-outs at the same time. Put
// this on the shared connection and two staff tapping within half a second could
// put the wrong person's name on an audit row — which, on the record of who did
// what, is the one thing it must never do.
//
// It is awaited INSIDE the existing serialised queue so the audit line can't
// overtake the tap it describes, and it can never fail the tap: record() returns
// a result object rather than throwing, and this swallows anything left over.
async function alsoLog({ siteKey, action, kind, detail }) {
  try {
    await recordActivity({ siteKey, action: action || "tap", kind, detail: detail || null, client: psb });
  } catch (e) {
    console.warn("[presence] activity line failed (tap still recorded):", e?.message || e);
  }
}

// ---- clock in / clock out ---------------------------------------------------
// The tablets already have a full clock-in flow built; it's switched off at every
// site today. When it's switched on, these give the events a faithful home. Same
// rules: fire-and-forget, no PIN stored, never throws.
export function recordClockIn({ siteKey, pin, photoPath = null }) {
  if (!active() || !siteKey || !pin) return Promise.resolve({ ok: false, skipped: true });
  queue = queue.then(async () => {
    try {
      const staffId = await passFor(pin, siteKey);
      if (!staffId) return { ok: false, error: "no pass" };
      const { error } = await psb.from("staff_shifts").insert({
        site: siteKey, started_at: new Date().toISOString(), source: "kiosk", photo_path: photoPath,
      });
      if (error) return { ok: false, error };
      // The tap itself is presence too — a clock-in is the strongest possible
      // evidence that somebody is in the building.
      await psb.from("staff_presence").insert({
        site: siteKey, kind: "clock_in", source: "clock", occurred_at: new Date().toISOString(), origin: "kiosk",
      });
      await alsoLog({ siteKey, action: "clockIn", kind: "action", detail: null });
      return { ok: true };
    } catch (e) { return { ok: false, error: e }; }
  }).catch(() => ({ ok: false }));
  return queue;
}

export function recordClockOut({ siteKey, pin }) {
  if (!active() || !siteKey || !pin) return Promise.resolve({ ok: false, skipped: true });
  queue = queue.then(async () => {
    try {
      const staffId = await passFor(pin, siteKey);
      if (!staffId) return { ok: false, error: "no pass" };
      // Close this person's open shift at this shop, if there is one.
      const { error } = await psb
        .from("staff_shifts")
        .update({ ended_at: new Date().toISOString() })
        .eq("staff_id", staffId)
        .eq("site", siteKey)
        .is("ended_at", null);
      if (error) return { ok: false, error };
      await psb.from("staff_presence").insert({
        site: siteKey, kind: "clock_out", source: "clock", occurred_at: new Date().toISOString(), origin: "kiosk",
      });
      await alsoLog({ siteKey, action: "clockOut", kind: "action", detail: null });
      return { ok: true };
    } catch (e) { return { ok: false, error: e }; }
  }).catch(() => ({ ok: false }));
  return queue;
}

// Test seam — lets the unit tests start from a clean slate.
export function _resetPresenceStateForTests() {
  lastPass = null;
  queue = Promise.resolve();
}
